import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
  House, Buildings, Receipt, CurrencyDollar, ShoppingCart, Tag,
  ChartLineUp, Users, SignOut, CoinVertical, Clock, GearSix, ArrowsClockwise,
  ClipboardText, Package, Notebook, MagnifyingGlass, Bell, CaretDown, CaretRight,
  Sparkle, HardDrives, CheckCircle, Wrench, PencilLine, ChatCircle, Calendar,
  ShieldCheck, Star, Warning, Command, IdentificationBadge,
} from "@phosphor-icons/react";
import CelsiusLogo from "@/components/CelsiusLogo";
import { NotificationBell } from "@/components/NotificationBell";
import CommandPalette from "@/components/CommandPalette";
import AICopilotDrawer from "@/components/AICopilotDrawer";
import KeyboardShortcuts from "@/components/KeyboardShortcuts";

// SAP Fiori 5-module structure
const MODULES = [
  {
    id: "home",
    label: "Home",
    icon: House,
    children: [
      { to: "/", label: "Dashboard", testid: "nav-dashboard", end: true },
    ],
  },
  {
    id: "projects",
    label: "Projects",
    icon: Buildings,
    sap: "PS",
    children: [
      { to: "/projects", label: "Projects", testid: "nav-projects" },
    ],
  },
  {
    id: "sales",
    label: "Sales & Receivables",
    icon: Receipt,
    sap: "SD",
    children: [
      { to: "/quotations", label: "Quotations", testid: "nav-quotations" },
      { to: "/sales-orders", label: "Sales Orders", testid: "nav-sales-orders" },
      { to: "/proforma-invoices", label: "Proforma Invoices", testid: "nav-proforma" },
      { to: "/price-catalog", label: "Price Catalog", testid: "nav-price-catalog" },
      { to: "/invoices", label: "Invoices", testid: "nav-invoices" },
      { to: "/ra-billing", label: "BOQ → RA Bills", testid: "nav-ra-billing" },
      { to: "/recurring", label: "Recurring Invoices", testid: "nav-recurring" },
      { to: "/amc-contracts", label: "AMC Contracts", testid: "nav-amc" },
      { to: "/payments", label: "Payments", testid: "nav-payments" },
      { to: "/aging", label: "Aging Report", testid: "nav-aging" },
      { to: "/sales", label: "Sales", testid: "nav-sales" },
    ],
  },
  {
    id: "procurement",
    label: "Procurement & Inventory",
    icon: ShoppingCart,
    sap: "MM",
    children: [
      { to: "/vendor-pos", label: "Vendor POs", testid: "nav-vendor-pos" },
      { to: "/daikin-pos", label: "Daikin POs (OFN)", testid: "nav-daikin-pos" },
      { to: "/material-requests", label: "Material Requests", testid: "nav-material-requests" },
      { to: "/purchases", label: "Purchases", testid: "nav-purchases" },
      { to: "/purchase-genius", label: "Purchase Genius", testid: "nav-purchase-genius" },
      { to: "/purchase-analytics", label: "Purchase Analytics", testid: "nav-purchase-analytics" },
      { to: "/expenses", label: "Expenses", testid: "nav-expenses" },
      { to: "/warehouses", label: "Warehouses", testid: "nav-warehouses" },
      { to: "/goods-receipts", label: "Goods Receipts", testid: "nav-goods-receipts" },
      { to: "/stock-transfers", label: "Stock Transfers", testid: "nav-stock-transfers" },
      { to: "/material-movement", label: "Material Movement", testid: "nav-material-movement" },
      { to: "/inventory", label: "Site Inventory", testid: "nav-inventory" },
      { to: "/materials-intel", label: "Materials Intelligence", testid: "nav-materials-intel" },
    ],
  },
  {
    id: "finance",
    label: "Finance & Reports",
    icon: ChartLineUp,
    sap: "FI",
    children: [
      { to: "/profit", label: "Profit & Loss", testid: "nav-profit" },
      { to: "/project-pnl", label: "Project P&L", testid: "nav-project-pnl" },
      { to: "/finance/balance-sheet", label: "Balance Sheet", testid: "nav-balance-sheet" },
      { to: "/finance/budgets", label: "Budgets", testid: "nav-budgets" },
      { to: "/finance/opening-balances", label: "Opening Balances", testid: "nav-opening-balances" },
      { to: "/reports/gst", label: "GST Reports (1 · 3B)", testid: "nav-gst-reports" },
    ],
  },
  {
    id: "service",
    label: "Service & Support",
    icon: Wrench,
    sap: "SV",
    children: [
      { to: "/service/equipment", label: "Equipment Registry", testid: "nav-equipment" },
      { to: "/service/snags", label: "Snag Lists", testid: "nav-snags" },
      { to: "/service/tickets", label: "Service Tickets", testid: "nav-tickets" },
      { to: "/service/commissioning", label: "Commissioning", testid: "nav-commissioning" },
      { to: "/service/ppm", label: "Preventive Maint.", testid: "nav-ppm" },
      { to: "/service/warranty", label: "Warranty Tracker", testid: "nav-warranty" },
      { to: "/service/csat", label: "Client Feedback (CSAT)", testid: "nav-csat" },
    ],
  },
];

const HR_MODULE = {
  id: "hr",
  label: "HR & Payroll",
  icon: IdentificationBadge,
  sap: "HCM",
  children: [
    { to: "/payroll", label: "Payroll & Salary Slips", testid: "nav-payroll", end: true },
    { to: "/payroll/attendance", label: "Attendance Register", testid: "nav-attendance" },
    { to: "/payroll/employees", label: "Employee Master", testid: "nav-employees" },
  ],
};

const ADMIN_MODULE = {
  id: "admin",
  label: "Administration",
  icon: GearSix,
  sap: "BC",
  children: [
    { to: "/users", label: "Team", testid: "nav-users" },
    { to: "/security/permissions", label: "Permissions", testid: "nav-permissions" },
    { to: "/security/audit", label: "Audit Log", testid: "nav-audit-log" },
    { to: "/approvals", label: "Approvals", testid: "nav-approvals" },
    { to: "/security/2fa", label: "Two-Factor Auth", testid: "nav-2fa" },
    { to: "/settings", label: "Company", testid: "nav-settings" },
  ],
};

function ModuleGroup({ mod, openId, setOpenId, currentPath }) {
  const containsActive = mod.children.some((c) =>
    c.end ? currentPath === c.to : currentPath.startsWith(c.to) && c.to !== "/"
  );
  const isOpen = openId === mod.id || containsActive;

  return (
    <div data-testid={`module-${mod.id}`}>
      <button
        type="button"
        onClick={() => setOpenId(isOpen && !containsActive ? null : mod.id)}
        data-testid={`module-toggle-${mod.id}`}
        className={`w-full flex items-center gap-2 px-3 py-[7px] text-[12.5px] font-normal border-l-[3px] transition-colors duration-75 ${
          containsActive
            ? "bg-[#E1EFFA] text-[#0F172A] border-[#2E75B6] font-semibold"
            : "text-[#323130] border-transparent hover:bg-[#F3F2F1]"
        }`}
      >
        <mod.icon size={14} weight={containsActive ? "fill" : "regular"} className={containsActive ? "text-[#2E75B6]" : "text-[#605E5C]"} />
        <span className="flex-1 text-left">{mod.label}</span>
        {mod.sap && (
          <span className="font-mono text-[9px] px-1 py-[2px] bg-[#EDEBE9] text-[#605E5C] rounded-[2px] tracking-[0.1em] leading-none">
            {mod.sap}
          </span>
        )}
        {isOpen ? <CaretDown size={9} className="text-[#605E5C]" /> : <CaretRight size={9} className="text-[#605E5C]" />}
      </button>
      {isOpen && (
        <div className="pb-1">
          {mod.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.end}
              data-testid={c.testid}
              className={({ isActive }) =>
                `group flex items-center gap-2 pl-10 pr-3 py-[5px] text-[12px] border-l-[3px] transition-colors duration-75 ${
                  isActive
                    ? "text-[#0F172A] border-[#2E75B6] bg-[#DEECF9] font-semibold"
                    : "text-[#605E5C] border-transparent hover:text-[#201F1E] hover:bg-[#F3F2F1]"
                }`
              }
            >
              <span className="w-[3px] h-[3px] rounded-full bg-current opacity-60" />
              <span>{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openId, setOpenId] = useState(null);
  const [time, setTime] = useState(new Date());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const isMac = useMemo(() => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform), []);

  const modules = useMemo(() => {
    const list = [...MODULES];
    if (user?.role === "admin") list.push(HR_MODULE, ADMIN_MODULE);
    return list;
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const timeStr = time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  const dateStr = time.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div
      className="h-screen grid grid-rows-[40px_1fr_20px] grid-cols-[240px_1fr] bg-[#FAF9F8]"
      data-testid="app-layout"
    >
      {/* ---- BC TOP NAV BAR ---- */}
      <header className="col-span-2 bg-[#2C3E50] text-white flex items-center h-10 px-3 z-20 border-b border-black/30">
        {/* App switcher / brand */}
        <div className="flex items-center gap-2.5 pr-3 mr-3 min-w-[196px]">
          <div className="w-6 h-6 grid grid-cols-2 grid-rows-2 gap-[2px] p-1 rounded-sm hover:bg-white/[0.08] cursor-pointer">
            <span className="bg-white/70" /><span className="bg-white/70" />
            <span className="bg-white/70" /><span className="bg-white/70" />
          </div>
          <div className="text-[13px] font-semibold tracking-[-0.005em] lowercase">celsius</div>
          <span className="text-[11px] text-white/50">Business Central</span>
        </div>

        {/* Search — BC centre — now opens Cmd+K palette */}
        <div className="flex-1 max-w-2xl">
          <button
            onClick={() => setPaletteOpen(true)}
            data-testid="global-search"
            className="w-full bg-white/[0.10] border border-white/[0.06] text-[12px] pl-8 pr-3 py-1 text-left text-white/70 hover:text-white hover:bg-white/[0.16] transition-colors rounded-sm relative flex items-center"
          >
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50" />
            <span className="flex-1">Search invoices, POs, projects · ask AI · type “?” to ask</span>
            <kbd className="hidden md:inline text-[9.5px] font-mono bg-white/10 border border-white/10 rounded px-1 py-0.5 text-white/70 ml-2">{isMac ? "⌘" : "Ctrl"} K</kbd>
          </button>
        </div>

        {/* Right cluster — BC icon-only buttons */}
        <div className="flex items-center gap-0.5 ml-3">
          <button
            onClick={() => setCopilotOpen(true)}
            data-testid="topbar-copilot"
            title="AI Copilot · Cmd+/"
            className="hidden md:flex items-center gap-1.5 px-2 py-1.5 text-[10.5px] tracking-[0.02em] text-white/80 hover:text-white bg-white/[0.08] hover:bg-white/[0.16] rounded-sm transition-colors"
          >
            <Sparkle size={12} weight="fill" className="text-[#FDBA74]" />
            <span>Ask Celsius</span>
          </button>
          <button
            className="hidden md:flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.06em] text-white/70 hover:text-white hover:bg-white/[0.08] rounded-sm transition-colors"
            data-testid="topbar-tenant"
            title="Current company"
          >
            <HardDrives size={12} />
            <span>Celsius India</span>
          </button>
          <NotificationBell />
          <button
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/[0.08] rounded-sm transition-colors"
            title="Settings"
          >
            <GearSix size={13} />
          </button>
          <div className="w-px h-5 bg-white/[0.14] mx-1.5" />
          <div className="flex items-center gap-2 px-1">
            <div className="w-6 h-6 bg-[#2E75B6] text-white flex items-center justify-center font-semibold text-[10px] rounded-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="hidden md:block leading-tight">
              <div className="text-[11.5px] font-medium text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[9px] text-white/50">{user?.role}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="ml-1 p-1.5 text-white/70 hover:text-white hover:bg-white/[0.08] rounded-sm transition-colors"
              title="Sign out"
            >
              <SignOut size={12} />
            </button>
          </div>
        </div>
      </header>

      {/* ---- BC SIDEBAR (white, thin) ---- */}
      <aside className="bg-white text-slate-800 flex flex-col border-r border-[#EDEBE9] overflow-y-auto">
        <nav className="flex-1 py-1 px-0">
          {modules.map((m) => (
            <ModuleGroup
              key={m.id}
              mod={m}
              openId={openId}
              setOpenId={setOpenId}
              currentPath={location.pathname}
            />
          ))}
        </nav>

        <div className="border-t border-[#EDEBE9] px-3 py-2 text-[10px] text-slate-500 flex items-center gap-2">
          <Sparkle size={12} weight="fill" className="text-[#2E75B6]" />
          <span className="tracking-[0.06em] uppercase">v2.7 · Fluent</span>
        </div>
      </aside>

      {/* ---- MAIN ---- */}
      <main className="min-w-0 overflow-y-auto bg-[#FAF9F8]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            className="min-h-full bg-[#FAF9F8]"
            style={{ willChange: "opacity, transform" }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ---- BC STATUS BAR ---- */}
      <footer className="col-span-2 bg-[#2C3E50] text-white/60 h-5 px-3 flex items-center justify-between text-[10px] tracking-[0.04em] border-t border-black/30 z-20">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <CheckCircle size={11} weight="fill" className="text-emerald-400" />
            <span>System OK</span>
          </span>
          <span className="opacity-40">|</span>
          <span>INR ₹ · IN-KA</span>
          <span className="opacity-40">|</span>
          <span>Client: Celsius</span>
        </div>
        <div className="flex items-center gap-3">
          <span>{dateStr} · {timeStr} IST</span>
          <span className="opacity-40">|</span>
          <span>GSTIN Enabled</span>
        </div>
      </footer>

      {/* ---- Cmd+K palette, AI Copilot drawer, keyboard shortcuts ---- */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AICopilotDrawer open={copilotOpen} onClose={() => setCopilotOpen(false)} />
      <KeyboardShortcuts
        onOpenPalette={() => setPaletteOpen(true)}
        onOpenCopilot={() => setCopilotOpen((v) => !v)}
      />
    </div>
  );
}
